"use client";

import React from "react";
import { ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import Reveal from "@/components/common/Reveal";
import { useLanguage } from "@/hooks/useLanguage";
import { type Lang } from "@/lib/i18n";
interface SupplyItem {
  id: string;
  title: string;
  description: string;
}
interface SupplyContent {
  eyebrow: string;
  heading: string;
  subheading: string;
  items: SupplyItem[];
  primaryCta: string;
  secondaryCta: string;
}
const content: Record<Lang, SupplyContent> = {
  en: {
    eyebrow: "B2B CAPABILITIES",
    heading: "Supply & Technical Support",
    subheading: "Specification matching, engineering guidance and custom fabrication for industrial buyers.",
    items: [{
      id: "1",
      title: "Technical Guidance",
      description: "Micron rating calculations, differential pressure estimation and alloy selection."
    }, {
      id: "2",
      title: "Specification Matching",
      description: "Direct cross-referencing against global standards including ASTM E2016 and ISO 9044."
    }, {
      id: "3",
      title: "OEM Fabrication",
      description: "Slit coil dimensions, stamped discs, welded cylinders and custom weave geometries."
    }],
    primaryCta: "Quality & Supply",
    secondaryCta: "About MutualFilter"
  },
  zh: {
    eyebrow: "工业供应能力",
    heading: "供应链与技术支持",
    subheading: "为工业采购商提供精准规格匹配、工程技术指导与定制加工支持。",
    items: [{
      id: "1",
      title: "技术指导支持",
      description: "提供微米级过滤精度计算、压降评估及耐蚀合金材质选型建议。"
    }, {
      id: "2",
      title: "标准规格匹配",
      description: "精准对照国际编织标准，包括 ASTM E2016 与 ISO 9044 技术规范。"
    }, {
      id: "3",
      title: "OEM 加工定制",
      description: "按需提供分条卷网、精密冲压滤片、焊接滤筒及特殊编织构型。"
    }],
    primaryCta: "质量与供应保障",
    secondaryCta: "关于 MutualFilter"
  },
  es: {
    eyebrow: "CAPACIDADES B2B",
    heading: "Suministro y soporte técnico",
    subheading: "Correspondencia de especificaciones, orientación técnica y fabricación a medida.",
    items: [{
      id: "1",
      title: "Orientación técnica",
      description: "Cálculo de micras, estimación de caída de presión y selección de aleaciones."
    }, {
      id: "2",
      title: "Correspondencia de normas",
      description: "Concordancia directa con normas globales como ASTM E2016 e ISO 9044."
    }, {
      id: "3",
      title: "Fabricación OEM",
      description: "Bobinas cortadas, discos troquelados, cilindros soldados y tejidos especiales."
    }],
    primaryCta: "Calidad y suministro",
    secondaryCta: "Sobre MutualFilter"
  },
  de: {
    eyebrow: "B2B-LEISTUNGEN",
    heading: "Lieferung & technischer Support",
    subheading: "Spezifikationsabgleich, ingenieurtechnische Beratung und individuelle Fertigung.",
    items: [{
      id: "1",
      title: "Technische Beratung",
      description: "Berechnung der Mikronwerte, Druckverlustabschätzung und Legierungsauswahl."
    }, {
      id: "2",
      title: "Spezifikationsabgleich",
      description: "Direkter Abgleich mit internationalen Standards wie ASTM E2016 und ISO 9044."
    }, {
      id: "3",
      title: "OEM-Fertigung",
      description: "Spaltbänder, gestanzte Filterscheiben, geschweißte Zylinder und Sondergewebe."
    }],
    primaryCta: "Qualität & Versorgung",
    secondaryCta: "Über MutualFilter"
  },
  fr: {
    eyebrow: "CAPACITÉS B2B",
    heading: "Approvisionnement et support technique",
    subheading: "Correspondance des spécifications, assistance technique et fabrication sur mesure.",
    items: [{
      id: "1",
      title: "Conseils techniques",
      description: "Calculs de seuil en microns, estimation de perte de charge et choix d'alliages."
    }, {
      id: "2",
      title: "Conformité aux normes",
      description: "Correspondance directe avec les normes internationales dont ASTM E2016 et ISO 9044."
    }, {
      id: "3",
      title: "Fabrication OEM",
      description: "Bobines refendues, disques emboutis, cylindres soudés et tissages sur mesure."
    }],
    primaryCta: "Qualité et approvisionnement",
    secondaryCta: "À propos de MutualFilter"
  },
  pt: {
    eyebrow: "CAPACIDADES B2B",
    heading: "Fornecimento e suporte técnico",
    subheading: "Correspondência de especificações, orientação técnica e fabricação sob medida.",
    items: [{
      id: "1",
      title: "Orientação técnica",
      description: "Cálculos de micragem, estimativa de queda de pressão e seleção de ligas."
    }, {
      id: "2",
      title: "Correspondência de normas",
      description: "Correspondência direta com padrões globais, incluindo ASTM E2016 e ISO 9044."
    }, {
      id: "3",
      title: "Fabricação OEM",
      description: "Bobinas fendidas, discos estampados, cilindros soldados e tecelagens sob medida."
    }],
    primaryCta: "Qualidade e fornecimento",
    secondaryCta: "Sobre a MutualFilter"
  }
};
export default function SupplySupport() {
  const {
    lang
  } = useLanguage();
  const current = content[lang] || content.en;
  return <section id="supplytechnicalsupport" data-nav="light" className="bg-background text-foreground mf-section border-b border-border">
      <div className="mf-container">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          {/* Left Column */}
          <div className="lg:col-span-5 lg:sticky lg:top-28">
            <Reveal>
              <div>
                <span className="text-xs uppercase tracking-widest font-mono text-muted-foreground block mb-3">
                  {current.eyebrow}
                </span>
                <h2 className="mf-h2 text-foreground mb-4">
                  {current.heading}
                </h2>
                <p className="text-base text-muted-foreground leading-relaxed mb-8">
                  {current.subheading}
                </p>

                {/* CTA Button Group */}
                <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
                  <Button asChild size="lg" className="px-6 min-h-[44px]">
                    <Link to="/QualitySupplyPage" className="inline-flex items-center gap-2">
                      <span>{current.primaryCta}</span>
                      <ArrowRight className="size-4 shrink-0 mf-arrow-nudge" aria-hidden="true" />
                    </Link>
                  </Button>

                  <Button asChild variant="outline" size="lg" className="px-6 min-h-[44px] border-border hover:bg-muted">
                    <Link to="/AboutPage">
                      {current.secondaryCta}
                    </Link>
                  </Button>
                </div>
              </div>
            </Reveal>
          </div>

          {/* Right Column: 3 Clean Key Points */}
          <div className="lg:col-span-7">
            <div className="border-t border-border">
              {current.items.map((item, i) => {
              const indexNum = `0${i + 1}`;
              return <Reveal key={item.id} delay={Math.min(i * 80, 300)}>
                    <div className="py-8 border-b border-border flex items-start gap-6 sm:gap-8">
                      <span className="font-mono text-sm sm:text-base font-semibold text-primary shrink-0 pt-0.5">
                        {indexNum}
                      </span>
                      <div className="min-w-0 flex-1">
                        <h3 className="font-default font-semibold text-foreground text-lg sm:text-xl tracking-tight mb-2">
                          {item.title}
                        </h3>
                        <p className="text-sm sm:text-base text-muted-foreground leading-relaxed">
                          {item.description}
                        </p>
                      </div>
                    </div>
                  </Reveal>;
            })}
            </div>
          </div>
        </div>
      </div>
    </section>;
}